import React, { useState, useEffect } from 'react';
import ReusableTable from '../components/adminTable';
import { useNavigate } from 'react-router-dom';
import { TableColumn, TableAction } from '../components/adminTable';
import { ArrowRightLeft, Users, CheckCircle, Clock, XCircle, AlertTriangle } from 'lucide-react';
import { useNotification } from '../../sharedComponents/Notifications';
import Pagination from '../components/Pagination';
import { Modal, MultiSelect } from '../components/adminModals';
import {
    submissionsByDeskService,
    submissionService,
    DeskItem,
    CategoryMemberDropdownItem,
    primaFacieService,
    Category,
    categoryAdminService,
    SubmissionStatus,
    isAuthorMoveDecision,
    statusForAuthorMoveDecision,
} from '../services/adminAuth';
import { useAuth } from '../../../auth/hooks/auth';
import { manuscriptReviewByDeskService, ManuscriptReviewByDeskItem, RequestRevisedCopyFromAuthorPayload } from '../services/adminAuth';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { formatDateForInput, parseDateFromInput } from '../../../utils/dateHelper';
import { formatRoleLabel } from '../../../utils/roleLabel';

// ─────────────────────────────────────────────────────────────────────────────

function ReviewedManuscriptsByDesk() {
    const { showNotification } = useNotification();
    const { user } = useAuth();

    const navigate = useNavigate();

    const isCategoryAdmin = user?.role === 'thematicarealeader';
    const isDess = user?.role === 'dess';
    const isTagChairman = user?.roleId === 5;
    const isDESS = user?.roleId === 9 || user?.role === 'dess';

    // Desk owner label for the page header (e.g. "Reviewed Manuscripts by Tag Chairman's Desk")
    const deskRoleLabel = isTagChairman ? 'Tag Chairman'
        : isDESS ? 'DESS'
        : isCategoryAdmin ? 'Thematic Area Leader'
        : formatRoleLabel(user?.displayRole);

    const needsReReview = (row: ManuscriptReviewByDeskItem) => row.decision === 'ReturnToReviewer';

    const [categories, setCategories] = useState<Category[]>([]);
    const [categoryAdminCategoryId, setCategoryAdminCategoryId] = useState<number | null>(null);
    const [initialized, setInitialized] = useState(false);

    const [manuscripts, setManuscripts] = useState<ManuscriptReviewByDeskItem[]>([]);
    const [loading, setLoading] = useState(true);

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    // Filter
    const [search, setSearch] = useState('');

    // Move modal
    const [isMoveModalOpen, setIsMoveModalOpen] = useState(false);
    const [moveTarget, setMoveTarget] = useState<ManuscriptReviewByDeskItem | null>(null);
    const [deskList, setDeskList] = useState<DeskItem[]>([]);
    const [deskListLoading, setDeskListLoading] = useState(false);
    const [selectedDeskId, setSelectedDeskId] = useState<number | ''>('');
    const [moveComment, setMoveComment] = useState('');
    const [isMoving, setIsMoving] = useState(false);

    // Move-to-Author modal (DESS / TAG Chairman, gated by finalDecision)
    const [isChangeStatusModalOpen, setIsChangeStatusModalOpen] = useState(false);
    const [statusTarget, setStatusTarget] = useState<ManuscriptReviewByDeskItem | null>(null);
    const [newStatus, setNewStatus] = useState<number | ''>('');
    const [statusComment, setStatusComment] = useState('');
    const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

    // Request Re-Review modal
    const [isReReviewModalOpen, setIsReReviewModalOpen] = useState(false);
    const [reReviewTarget, setReReviewTarget] = useState<ManuscriptReviewByDeskItem | null>(null);
    const [reReviewComment, setReReviewComment] = useState('');
    const [reReviewDeadline, setReReviewDeadline] = useState<string>('');
    const [isRequestingReReview, setIsRequestingReReview] = useState(false);

    // Assign Reviewer Evaluation Panel modal
    const [isAssignPanelOpen, setIsAssignPanelOpen] = useState(false);
    const [assignPanelTarget, setAssignPanelTarget] = useState<ManuscriptReviewByDeskItem | null>(null);
    const [categoryMembers, setCategoryMembers] = useState<CategoryMemberDropdownItem[]>([]);
    const [fetchingMembers, setFetchingMembers] = useState(false);
    const [selectedMemberIds, setSelectedMemberIds] = useState<(string | number)[]>([]);
    const [isAssigning, setIsAssigning] = useState(false);

    // Request Revised Copy modal
    const [isRevisedCopyModalOpen, setIsRevisedCopyModalOpen] = useState(false);
    const [revisedCopyTarget, setRevisedCopyTarget] = useState<ManuscriptReviewByDeskItem | null>(null);
    const [revisedCopyComment, setRevisedCopyComment] = useState('');
    const [isRequestingRevisedCopy, setIsRequestingRevisedCopy] = useState(false);

    // ── Fetch ──────────────────────────────────────────────────────────────────
    useEffect(() => {
        if (!initialized) return;
        if (isCategoryAdmin && categoryAdminCategoryId === null) return;
        
        const handler = setTimeout(() => {
            fetchManuscripts();
        }, 400);
        return () => clearTimeout(handler);
    }, [initialized, currentPage, pageSize, search, isCategoryAdmin, categoryAdminCategoryId]);

    // ── Bootstrap Category Admin ──────────────────────────────────────────────────
    useEffect(() => {
        const init = async () => {
            if (!isCategoryAdmin) {
                setInitialized(true);
                return;
            }
            
            try {
                const res = await categoryAdminService.getCategoriesForCategoryAdmin();
                if (res.isSuccess && res.data && res.data.length > 0) {
                    setCategories(res.data);
                    setCategoryAdminCategoryId(res.data[0].id);
                } else {
                    setCategoryAdminCategoryId(0);
                }
            } catch {
                setCategoryAdminCategoryId(0);
            } finally {
                setInitialized(true);
            }
        };
        init();
    }, [isCategoryAdmin]);

    const fetchManuscripts = async () => {
        // Don't fetch if category admin and no valid category
        if (isCategoryAdmin && (categoryAdminCategoryId === null || categoryAdminCategoryId === 0)) {
            setLoading(false);
            setManuscripts([]);
            setTotalRecords(0);
            setTotalPages(0);
            return;
        }

        setLoading(true);
        try {
            const response = await manuscriptReviewByDeskService.getManuscriptReviewByDesk({
                pageNumber: currentPage,
                pageSize,
                sortBy: '',
                sortDescending: true,
                search,
                categoryId: isCategoryAdmin ? (categoryAdminCategoryId ?? 0) : 0, // Use category ID if admin
            });

            if (response.isSuccess && response.data) {
                setManuscripts(response.data.data);
                setTotalRecords(response.data.totalRecords);
                setTotalPages(response.data.totalPages);
            } else {
                showNotification('error', {
                    title: 'Error',
                    message: response.message || 'Failed to fetch reviewed manuscripts',
                });
            }
        } catch (err) {
            showNotification('error', {
                title: 'Error',
                message: 'An error occurred while fetching reviewed manuscripts',
            });
        } finally {
            setLoading(false);
        }
    };

    // ── Move handlers ──────────────────────────────────────────────────────────

    const openMoveModal = async (item: ManuscriptReviewByDeskItem) => {
        setMoveTarget(item);
        setSelectedDeskId('');
        setMoveComment('');
        setIsMoveModalOpen(true);
        setDeskListLoading(true);
        try {
            const response = await submissionsByDeskService.getDeskList();
            setDeskList(response.isSuccess && response.data ? response.data : []);
        } catch {
            showNotification('error', { title: 'Error', message: 'Failed to load desk list' });
        } finally {
            setDeskListLoading(false);
        }
    };

    const closeMoveModal = () => {
        setIsMoveModalOpen(false);
        setMoveTarget(null);
        setSelectedDeskId('');
        setMoveComment('');
    };

    const handleMoveManuscript = async () => {
        if (!moveTarget || selectedDeskId === '') return;
        setIsMoving(true);
        try {
            const response = await manuscriptReviewByDeskService.moveManuscriptReview({
                manuscriptReviewId: moveTarget.id,
                comment: moveComment,
                toWorkflowStage: selectedDeskId as number,
            });
            if (response.isSuccess) {
                showNotification('success', { title: 'Success', message: 'Manuscript moved successfully' });
                closeMoveModal();
                fetchManuscripts();
            } else {
                showNotification('error', { title: 'Error', message: response.message || 'Failed to move manuscript' });
            }
        } catch {
            showNotification('error', { title: 'Error', message: 'An error occurred while moving the manuscript' });
        } finally {
            setIsMoving(false);
        }
    };

    // ── Move-to-Author handlers (DESS / TAG Chairman) ──────────────────────────

    const openChangeStatusModal = (row: ManuscriptReviewByDeskItem) => {
        setStatusTarget(row);
        setStatusComment('');
        const dec = row.decision;
        if (isAuthorMoveDecision(dec)) {
            setNewStatus(statusForAuthorMoveDecision(dec).value);
        } else {
            setNewStatus('');
        }
        setIsChangeStatusModalOpen(true);
    };

    const closeChangeStatusModal = () => {
        if (isUpdatingStatus) return;
        setIsChangeStatusModalOpen(false);
        setStatusTarget(null);
        setNewStatus('');
        setStatusComment('');
    };

    const handleChangeStatus = async () => {
        if (!statusTarget || newStatus === '') return;
        setIsUpdatingStatus(true);
        try {
            const response = await submissionService.communicateSubmissionStatusToAuthor({
                submissionId: statusTarget.submissionId,
                comment: statusComment.trim() || '',
                submissionStatus: newStatus as number,
            });
            if (response.isSuccess) {
                showNotification('success', { title: 'Success', message: 'Author notified successfully' });
                closeChangeStatusModal();
                fetchManuscripts();
            } else {
                showNotification('error', { title: 'Error', message: response.message || 'Failed to communicate with the author' });
            }
        } catch {
            showNotification('error', { title: 'Error', message: 'An error occurred while communicating with the author' });
        } finally {
            setIsUpdatingStatus(false);
        }
    };

    // ── Request Re-Review handlers ─────────────────────────────────────────────

    const openReReviewModal = (item: ManuscriptReviewByDeskItem) => {
        setReReviewTarget(item);
        setReReviewComment('');
        setReReviewDeadline('');
        setIsReReviewModalOpen(true);
    };

    const closeReReviewModal = () => {
        if (isRequestingReReview) return;
        setIsReReviewModalOpen(false);
        setReReviewTarget(null);
        setReReviewComment('');
        setReReviewDeadline('');
    };

    const handleRequestReReview = async () => {
        if (!reReviewTarget || !reReviewComment.trim() || !reReviewDeadline) return;
        setIsRequestingReReview(true);
        try {
            const response = await submissionService.requestReReview({
                reviewAssignmentId: reReviewTarget.reviewAssignmentId,
                comment: reReviewComment.trim(),
                deadLineDate: parseDateFromInput(reReviewDeadline),
            });
            if (response.isSuccess) {
                showNotification('success', { title: 'Success', message: response.message || 'Re-review requested successfully' });
                closeReReviewModal();
                fetchManuscripts();
            } else {
                showNotification('error', { title: 'Failed', message: response.message || 'Could not request re-review' });
            }
        } catch {
            showNotification('error', { title: 'Error', message: 'An unexpected error occurred while requesting re-review' });
        } finally {
            setIsRequestingReReview(false);
        }
    };

    // ── Assign Reviewer Evaluation Panel handlers ──────────────────────────────

    const openAssignPanelModal = async (item: ManuscriptReviewByDeskItem) => {
        setAssignPanelTarget(item);
        setSelectedMemberIds([]);
        setIsAssignPanelOpen(true);
        setFetchingMembers(true);
        try {
            const response = await primaFacieService.getCategoryMemberDropdown(
                item.categoryId ?? 0
            );
            if (response.isSuccess && response.data) {
                setCategoryMembers(response.data);
            } else {
                showNotification('error', { title: 'Error', message: response.message || 'Failed to load members' });
                setCategoryMembers([]);
            }
        } catch {
            showNotification('error', { title: 'Error', message: 'An unexpected error occurred while loading members' });
            setCategoryMembers([]);
        } finally {
            setFetchingMembers(false);
        }
    };

    const closeAssignPanelModal = () => {
        setIsAssignPanelOpen(false);
        setAssignPanelTarget(null);
        setSelectedMemberIds([]);
        setCategoryMembers([]);
    };

    const handleAssignPanel = async () => {
        // Strict 3-member panel rule (kept for reference, temporarily relaxed — any 1+ allowed):
        // if (!assignPanelTarget || selectedMemberIds.length < 3) return;
        if (!assignPanelTarget || selectedMemberIds.length < 1) return;
        setIsAssigning(true);
        try {
            const assignedMembers = selectedMemberIds.map(id => {
                const member = categoryMembers.find(m => m.id === id);
                return { memberId: id.toString(), name: member?.name ?? '' };
            });

            const response = await manuscriptReviewByDeskService.assignReviewerEvaluationPanel({
                manuscriptReviewId: assignPanelTarget.id,
                assignedMembers,
            });

            if (response.isSuccess) {
                const results = response.data ?? [];
                const failed = results.filter(r => !r.successful);
                if (failed.length > 0) {
                    showNotification('error', {
                        title: 'Partial Assignment',
                        message: `${failed.length} member(s) could not be assigned: ${failed.map(f => f.reason).join(', ')}`,
                    });
                } else {
                    showNotification('success', { title: 'Success', message: 'Reviewer Evaluation Panel assigned successfully' });
                }
                closeAssignPanelModal();
                fetchManuscripts();
            } else {
                showNotification('error', { title: 'Assignment Failed', message: response.message || 'Failed to assign panel' });
            }
        } catch {
            showNotification('error', { title: 'Error', message: 'An unexpected error occurred during assignment' });
        } finally {
            setIsAssigning(false);
        }
    };

    const openRevisedCopyModal = (item: ManuscriptReviewByDeskItem) => {
        setRevisedCopyTarget(item);
        setRevisedCopyComment('');
        setIsRevisedCopyModalOpen(true);
    };

    const closeRevisedCopyModal = () => {
        if (isRequestingRevisedCopy) return;
        setIsRevisedCopyModalOpen(false);
        setRevisedCopyTarget(null);
        setRevisedCopyComment('');
    };

    const handleRequestRevisedCopy = async () => {
        if (!revisedCopyTarget || !revisedCopyComment.trim()) return;
        setIsRequestingRevisedCopy(true);
        try {
            const submissionId =
                revisedCopyTarget.submissionId ?? "";

            const response = await manuscriptReviewByDeskService.requestRevisedCopyFromAuthor({
                submissionId,
                comment: revisedCopyComment.trim(),
            });
            if (response.isSuccess) {
                showNotification('success', {
                    title: 'Success',
                    message: response.message || 'Revised copy requested successfully',
                });
                closeRevisedCopyModal();
                fetchManuscripts();
            } else {
                showNotification('error', {
                    title: 'Failed',
                    message: response.message || 'Could not request revised copy',
                });
            }
        } catch {
            showNotification('error', {
                title: 'Error',
                message: 'An unexpected error occurred',
            });
        } finally {
            setIsRequestingRevisedCopy(false);
        }
    };

    const formatDecision = (decision: string) => {
        switch (decision) {
            case 'AcceptableForPublication':    return 'Acceptable for Publication';
            case 'NotAcceptableForPublication': return 'Not Acceptable for Publication';
            case 'AcceptableForFunding':        return 'Acceptable for Funding';
            case 'NotAcceptableForFunding':     return 'Not Acceptable for Funding';
            case 'ReturnToReviewer':            return 'Return to Reviewer';
            case 'ReturnToAuthorForRevisedCopy':              return 'Return to Author for Revised Copy';
            default:                            return decision;
        }
    };

    const isNotAcceptableDecision = (decision: string) =>
        decision === 'NotAcceptableForPublication' || decision === 'NotAcceptableForFunding' || decision === 'ReturnToReviewer';

    const getReviewerInitials = (name: string) => {
        const parts = name.trim().split(/\s+/).filter(Boolean);
        if (parts.length === 0) return '?';
        if (parts.length === 1) return parts[0][0]?.toUpperCase() ?? '?';
        return `${parts[0][0] ?? ''}${parts[parts.length - 1][0] ?? ''}`.toUpperCase();
    };

    const getManuscriptTitle = (row: ManuscriptReviewByDeskItem) =>
        row.title ?? row.reviewAssignment?.submission?.title ?? '—';

    const getAuthorName = (row: ManuscriptReviewByDeskItem) => {
        const sub = row.reviewAssignment?.submission;
        const person = sub?.author?.person;
        return row.authorName ?? sub?.authors ?? person?.fullName ?? '—';
    };

    const getThematicArea = (row: ManuscriptReviewByDeskItem) =>
        row.field ?? row. categoryName ?? '—';

    const getReviewerName = (row: ManuscriptReviewByDeskItem) =>
        row.reviewerName ?? row.reviewAssignment?.reviewer?.person?.fullName ?? '—';

    const getTotalScore = (row: ManuscriptReviewByDeskItem) =>
        row.totalScore ?? row.score?.totalScore;

    // ── Columns ────────────────────────────────────────────────────────────────

    const columns: TableColumn<ManuscriptReviewByDeskItem>[] = [
        {
            key: 'submissionNumber',
            label: 'ID',
            render: (_, row) => (
                <span className="text-gray-500 text-sm font-mono">
                    {row.submissionNumber ?? row.reviewAssignment?.submission?.submissionNumber ?? '—'}
                </span>
            ),
        },
        {
            key: 'title',
            label: 'Title / Author',
            render: (_, row) => (
                <div>
                    <div className="font-medium text-gray-900 mb-0.5">{getManuscriptTitle(row)}</div>
                    <div className="text-sm text-gray-500">{getAuthorName(row)}</div>
                </div>
            ),
        },
        {
            key: 'field',
            label: 'Thematic Area',
            render: (_, row) => (
                <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-700">
                    {getThematicArea(row)}
                </span>
            ),
        },
        {
            key: 'reviewerName',
            label: 'Reviewer',
            render: (_, row) => {
                const name = getReviewerName(row);
                const reviewer = row.reviewAssignment?.reviewer?.person;
                const initials = name !== '—'
                    ? getReviewerInitials(name)
                    : `${reviewer?.firstName?.[0] ?? ''}${reviewer?.lastName?.[0] ?? ''}` || '?';
                return (
                    <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-green-100 flex items-center justify-center text-green-700 font-bold text-xs flex-shrink-0">
                            {initials}
                        </div>
                        <span className="text-sm text-gray-700">{name}</span>
                    </div>
                );
            },
        },
        {
            key: 'totalScore',
            label: 'Review Score',
            render: (_, row) => {
                const score = getTotalScore(row);
                if (score == null) return <span className="text-sm text-gray-400">—</span>;
                return (
                    <span className="text-sm font-semibold text-gray-800">
                        {Number(score).toFixed(0)}%
                    </span>
                );
            },
        },
        {
            key: 'completedReviewerEvaluation',
            label: 'Completed Reviewer Evaluation?',
            render: (value, row) => {
                const isCompleted = value as boolean;
                return (
                    <div className="flex flex-col items-start gap-1">
                        {isCompleted ? (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-50 text-green-700 border border-green-200">
                                <CheckCircle className="w-3.5 h-3.5" />
                                Yes
                            </span>
                        ) : (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-50 text-red-600 border border-red-200">
                                <XCircle className="w-3.5 h-3.5" />
                                No
                            </span>
                        )}
                        {row.decision && (
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium ${
                                isNotAcceptableDecision(row.decision)
                                    ? 'bg-red-50 text-red-700 border border-red-100'
                                    : 'bg-green-50 text-green-700 border border-green-100'
                            }`}>
                                {formatDecision(row.decision)}
                            </span>
                        )}
                    </div>
                );
            },
        },
    ];

    const actions: TableAction<ManuscriptReviewByDeskItem>[] = [
        {
            variant: 'dropdown',
            onClick: () => undefined,
            dropdownItems: [
                {
                    label: 'View Review Details',
                    onClick: (row) => {
                        navigate(`/admin/review-form/${row.reviewAssignmentId}`);
                    },
                    className: 'text-blue-600',
                },
                {
                    label: 'Move Reviewed Manuscript',
                    onClick: openMoveModal,
                    className: (row) => {
                        if (isCategoryAdmin) return 'hidden';
                        if (isDESS) return row.completedReviewerEvaluation !== true ? 'text-blue-600' : 'hidden';
                        if (isTagChairman && needsReReview(row)) return 'hidden';
                        return 'text-blue-600';
                    },
                },
                {
                    label: 'Request Re-Review',
                    onClick: openReReviewModal,
                    className: (row) =>
                        isTagChairman && needsReReview(row)
                            ? 'text-[#088A61] font-semibold'
                            : 'hidden',
                },
                {
                    label: 'Assign Reviewer Evaluation Panel',
                    onClick: openAssignPanelModal,
                    className: isCategoryAdmin ? 'text-blue-600' : 'hidden',
                },
                {
                    label: 'Request Revised Copy From Author',
                    onClick: openRevisedCopyModal,
                    className: (row) =>
                        isDess && row.decision === 'ReturnToAuthorForRevisedCopy'
                            ? 'text-[#088A61] font-semibold'
                            : 'hidden',
                },
                {
                    label: 'Communicate Decision to Author',
                    onClick: openChangeStatusModal,
                    className: (row) =>
                        isDESS && row.decision === 'NotAcceptableForPublication'
                            ? 'text-[#088A61] font-semibold'
                            : 'hidden',
                },
            ],
            title: 'Actions',
        },
    ];

    // ── Render ─────────────────────────────────────────────────────────────────

    return (
        <div className="w-full">
            {/* Header */}
            <div className="mb-6 flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                        {deskRoleLabel ? `Reviewed Manuscripts by ${deskRoleLabel}'s Desk` : 'Reviewed Manuscripts by Desk'}
                    </h1>
                    <p className="text-gray-500 text-sm sm:text-base">
                        View and manage manuscript reviews currently at your desk.
                    </p>
                </div>
            </div>

            {/* Show empty state for category admin with no category */}
            {isCategoryAdmin && categoryAdminCategoryId === 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center mb-6">
                    <AlertTriangle className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">No Thematic Area Assigned</h3>
                    <p className="text-gray-600">
                        You haven't been assigned to any thematic area yet. Please contact an administrator.
                    </p>
                </div>
            )}

            {/* Table */}
            <ReusableTable
                title="Desk Manuscript Reviews"
                subTitle="Manuscript reviews assigned to your desk"
                columns={columns}
                data={manuscripts}
                actions={actions}
                loading={loading}
                searchPlaceholder="Search by title, author, or ID..."
                onSearch={(q) => { setSearch(q); setCurrentPage(1); }}
                emptyMessage="No reviewed manuscripts found at your desk."
                showSearch={true}
                showFilter={false}
            />

            {/* Pagination */}
            {!loading && totalRecords > 0 && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalRecords={totalRecords}
                    pageSize={pageSize}
                    onPageChange={(p) => setCurrentPage(p)}
                    onPageSizeChange={(s) => { setPageSize(s); setCurrentPage(1); }}
                />
            )}

            {/* ── Move Modal ─────────────────────────────────────────────────────── */}
            <Modal
                isOpen={isMoveModalOpen}
                onClose={closeMoveModal}
                title="Move Reviewed Manuscript"
                description="You're about to move this manuscript review to another desk. Please confirm the destination and optionally leave a comment."
                maxWidth="md"
            >
                <div className="space-y-4 mt-2">
                    {moveTarget && (
                        <div className="bg-gray-50 rounded-lg px-4 py-3 border border-gray-100">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Manuscript</p>
                            <p className="text-sm font-medium text-gray-900 line-clamp-2">
                                {getManuscriptTitle(moveTarget)}
                            </p>
                            {moveTarget.decision && (
                                <span className={`inline-flex items-center py-0.5 rounded text-[11px] font-medium ${
                                    isNotAcceptableDecision(moveTarget.decision)
                                        ? 'text-red-700'
                                        : 'text-green-700'
                                }`}>
                                    {formatDecision(moveTarget.decision)}
                                </span>
                            )}

                        </div>
                    )}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Move To <span className="text-red-500">*</span>
                        </label>
                        {deskListLoading ? (
                            <div className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-400 flex items-center gap-2">
                                <svg className="animate-spin h-4 w-4 text-gray-400" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                                Loading desks...
                            </div>
                        ) : (
                            <select
                                value={selectedDeskId}
                                onChange={(e) => setSelectedDeskId(e.target.value === '' ? '' : Number(e.target.value))}
                                className="block w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600 focus:border-transparent"
                            >
                                <option value="">Select destination desk...</option>
                                {deskList.map((desk) => (
                                    <option key={desk.id} value={desk.id}>{desk.name}</option>
                                ))}
                            </select>
                        )}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Comment <span className="text-gray-400 font-normal">(optional)</span>
                        </label>
                        <textarea
                            value={moveComment}
                            onChange={(e) => setMoveComment(e.target.value)}
                            placeholder="Add a note about this move..."
                            rows={3}
                            className="block w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600 focus:border-transparent resize-none"
                        />
                    </div>
                    <div className="flex items-center justify-end gap-3 pt-2">
                        <button onClick={closeMoveModal} disabled={isMoving}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                            Cancel
                        </button>
                        <button onClick={handleMoveManuscript} disabled={isMoving || selectedDeskId === ''}
                            className="px-5 py-2 bg-green-700 text-white rounded-lg text-sm font-medium hover:bg-green-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
                            {isMoving ? (
                                <>
                                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                    </svg>
                                    Moving...
                                </>
                            ) : (
                                <><ArrowRightLeft className="w-4 h-4" /> Move Manuscript</>
                            )}
                        </button>
                    </div>
                </div>
            </Modal>

            {/* ── Assign Reviewer Evaluation Panel Modal ─────────────────────────── */}
            <Modal
                isOpen={isAssignPanelOpen}
                onClose={closeAssignPanelModal}
                title="Assign Reviewer Evaluation Panel"
                description="Select one or more thematic area members to form the Reviewer Evaluation panel for this manuscript."
                maxWidth="md"
            >
                <div className="space-y-4 mt-2">
                    {assignPanelTarget && (
                        <div className="bg-gray-50 rounded-lg px-4 py-3 border border-gray-100">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Manuscript</p>
                            <p className="text-sm font-medium text-gray-900 line-clamp-2">
                                {getManuscriptTitle(assignPanelTarget)}
                            </p>
                            <p className="text-xs text-gray-400 mt-0.5">
                                {getThematicArea(assignPanelTarget)}
                            </p>
                        </div>
                    )}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Panel Members <span className="text-red-500">*</span>
                            <span className="text-gray-400 font-normal ml-1">(select 1 or more)</span>
                        </label>
                        {fetchingMembers ? (
                            <div className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-400 flex items-center gap-2">
                                <svg className="animate-spin h-4 w-4 text-gray-400" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                                Loading members...
                            </div>
                        ) : categoryMembers.length === 0 ? (
                            <div className="w-full px-3 py-3 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-400 text-center italic">
                                No members available for this thematic area
                            </div>
                        ) : (
                            <MultiSelect
                                options={categoryMembers.map(m => ({ value: m.id, label: m.name }))}
                                selectedValues={selectedMemberIds}
                                onChange={setSelectedMemberIds}
                                placeholder="Select panel members..."
                            />
                        )}
                        {selectedMemberIds.length > 0 && (
                            <>
                                {/* Strict 3-member hint (kept for reference, temporarily relaxed):
                                <p className={`text-xs mt-1.5 font-medium ${selectedMemberIds.length >= 3 ? 'text-green-700' : 'text-amber-600'}`}>
                                    {selectedMemberIds.length} selected
                                    {selectedMemberIds.length < 3 && ` — select at least ${3 - selectedMemberIds.length} more`}
                                </p> */}
                                <p className="text-xs mt-1.5 font-medium text-green-700">
                                    {selectedMemberIds.length} selected
                                </p>
                            </>
                        )}
                    </div>

                    {selectedMemberIds.length > 0 && (
                        <div className="bg-gray-50 rounded-lg p-3 border border-gray-100 space-y-1.5">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Selected Panel</p>
                            {selectedMemberIds.map(id => {
                                const member = categoryMembers.find(m => m.id === id);
                                return member ? (
                                    <div key={String(id)} className="flex items-center gap-2">
                                        <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center text-green-700 font-bold text-xs flex-shrink-0">
                                            {member.name.charAt(0)}
                                        </div>
                                        <span className="text-sm text-gray-700">{member.name}</span>
                                    </div>
                                ) : null;
                            })}
                        </div>
                    )}

                    <div className="flex items-center justify-end gap-3 pt-2">
                        <button onClick={closeAssignPanelModal} disabled={isAssigning}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                            Cancel
                        </button>
                        <button onClick={handleAssignPanel} disabled={isAssigning || selectedMemberIds.length === 0 /* was: < 3 (strict 3-member panel) */}
                            className="px-5 py-2 bg-green-700 text-white rounded-lg text-sm font-medium hover:bg-green-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
                            {isAssigning ? (
                                <>
                                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                    </svg>
                                    Assigning...
                                </>
                            ) : (
                                <><Users className="w-4 h-4" /> Assign Panel</>
                            )}
                        </button>
                    </div>
                </div>
            </Modal>

            {/* ── Request Re-Review Modal ───────────────────────────────────────── */}
            <Modal
                isOpen={isReReviewModalOpen}
                onClose={closeReReviewModal}
                title="Request Re-Review"
                description="Send the manuscript back to the reviewer for re-assessment with a reason and a deadline."
            >
                <div className="space-y-4">
                    <div>
                        <p className="text-sm text-gray-600">
                            Manuscript: <span className="font-semibold text-gray-900">{reReviewTarget?.title}</span>
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                            Reviewer: {reReviewTarget?.reviewerName ?? '—'}
                        </p>
                    </div>

                    <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">
                            Reason / Comment <span className="text-red-500">*</span>
                        </label>
                        <textarea
                            value={reReviewComment}
                            onChange={(e) => setReReviewComment(e.target.value)}
                            placeholder="Explain why the manuscript is being returned for re-review…"
                            rows={4}
                            className="block w-full px-2.5 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#088A61] focus:border-transparent resize-none"
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">
                            Deadline <span className="text-red-500">*</span>
                        </label>
                        <DatePicker
                            selected={reReviewDeadline ? new Date(reReviewDeadline) : null}
                            onChange={(date: Date | null) => setReReviewDeadline(date ? formatDateForInput(date.toISOString()) : '')}
                            showTimeSelect
                            timeFormat="HH:mm"
                            timeIntervals={15}
                            dateFormat="dd/MM/yyyy h:mm aa"
                            minDate={new Date()}
                            className="w-full px-2.5 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#088A61] focus:border-transparent"
                            placeholderText="DD/MM/YYYY HH:MM"
                            wrapperClassName="w-full"
                        />
                    </div>

                    <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-100">
                        <button
                            onClick={closeReReviewModal}
                            disabled={isRequestingReReview}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleRequestReReview}
                            disabled={isRequestingReReview || !reReviewComment.trim() || !reReviewDeadline}
                            className="px-5 py-2 bg-[#088A61] text-white rounded-lg text-sm font-bold hover:bg-[#067451] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isRequestingReReview ? 'Submitting…' : 'Request Re-Review'}
                        </button>
                    </div>
                </div>
            </Modal>

            {/* ── Request Revised Copy Modal ───────────────────────────────────────── */}
            <Modal
                isOpen={isRevisedCopyModalOpen}
                onClose={closeRevisedCopyModal}
                title="Request Revised Copy From Author"
                description="Send a request to the author to submit a revised copy of this manuscript."
            >
                <div className="space-y-4">
                    {revisedCopyTarget && (
                        <div className="bg-gray-50 rounded-lg px-4 py-3 border border-gray-100">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">
                                Manuscript
                            </p>
                            <p className="text-sm font-medium text-gray-900 line-clamp-2">
                                {getManuscriptTitle(revisedCopyTarget)}
                            </p>
                            <p className="text-xs text-gray-500 mt-0.5">
                                Author: {getAuthorName(revisedCopyTarget)}
                            </p>
                            <p className="text-xs text-gray-500 mt-0.5">
                                Reviewer: {getReviewerName(revisedCopyTarget)}
                            </p>
                            {revisedCopyTarget.decision && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-yellow-50 text-yellow-700 border border-yellow-100 mt-1">
                                    {formatDecision(revisedCopyTarget.decision)}
                                </span>
                            )}
                        </div>
                    )}

                    <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">
                            Comment <span className="text-red-500">*</span>
                        </label>
                        <textarea
                            value={revisedCopyComment}
                            onChange={(e) => setRevisedCopyComment(e.target.value)}
                            placeholder="Provide instructions or reasons for requesting a revised copy…"
                            rows={4}
                            className="block w-full px-2.5 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#088A61] focus:border-transparent resize-none"
                        />
                    </div>

                    <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-100">
                        <button
                            onClick={closeRevisedCopyModal}
                            disabled={isRequestingRevisedCopy}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleRequestRevisedCopy}
                            disabled={isRequestingRevisedCopy || !revisedCopyComment.trim()}
                            className="px-5 py-2 bg-[#088A61] text-white rounded-lg text-sm font-bold hover:bg-[#067451] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isRequestingRevisedCopy ? 'Submitting…' : 'Request Revised Copy'}
                        </button>
                    </div>
                </div>
            </Modal>

            {/* ── Move Submission to Author Modal (DESS / TAG Chairman) ── */}
            <Modal
                isOpen={isChangeStatusModalOpen}
                onClose={closeChangeStatusModal}
                title="Communicate Submission Status"
                description="Move the reviewed manuscript back to the author with an optional comment."
            >
                <div className="space-y-4 mt-2">
                    {statusTarget && (
                        <div className="bg-gray-50 rounded-lg px-4 py-3 border border-gray-100">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Submission</p>
                            <p className="text-sm font-medium text-gray-900 line-clamp-2">{statusTarget.title ?? '—'}</p>
                            <p className="text-xs text-gray-400 mt-0.5">{statusTarget.submissionNumber ?? '—'}</p>
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Submission Status <span className="text-red-500">*</span>
                        </label>
                        <select
                            value={newStatus}
                            onChange={(e) => setNewStatus(e.target.value === '' ? '' : Number(e.target.value))}
                            className="block w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600 focus:border-transparent"
                        >
                            <option value="">Select Status...</option>
                            {(() => {
                                const dec = statusTarget?.decision;
                                if (!isAuthorMoveDecision(dec)) return null;
                                const opt = statusForAuthorMoveDecision(dec);
                                return <option value={opt.value}>{opt.label}</option>;
                            })()}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Comment <span className="text-gray-400 font-normal">(optional)</span>
                        </label>
                        <textarea
                            value={statusComment}
                            onChange={(e) => setStatusComment(e.target.value)}
                            placeholder="Add a note for the author…"
                            rows={3}
                            className="block w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600 focus:border-transparent resize-none"
                        />
                    </div>

                    <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-100">
                        <button
                            onClick={closeChangeStatusModal}
                            disabled={isUpdatingStatus}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleChangeStatus}
                            disabled={isUpdatingStatus || newStatus === ''}
                            className="px-5 py-2 bg-[#088A61] text-white rounded-lg text-sm font-bold hover:bg-[#067451] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isUpdatingStatus ? 'Moving…' : 'Move Submission'}
                        </button>
                    </div>
                </div>
            </Modal>

        </div>
    );
}

export default ReviewedManuscriptsByDesk;
